import React, { useState, useEffect, useCallback } from 'react';
import { FiFileText, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import { API_URLS } from '../config';
import { apiRequest } from '../utils/apiRequest';
import { useAuth } from '../context/AuthContext';

/**
 * ServiceSelection component — for selecting services in a grid of checkboxes.
 * Styled to look like the Competency component.
 */
const ServiceSelection = ({ value = [], onChange, readOnly = false, type = 'customer' }) => {
  const { token } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = type === 'customer' 
        ? `${API_URLS.CUSTOMERS}/options/master-data`
        : `${API_URLS.VENDORS}/options/master-data`;
        
      const res = await apiRequest(url, {}, token);
      if (res?.ok) {
        setServices(res.data.services || []);
      } else {
        setError('Could not load services.');
      }
    } catch {
      setError('Network error loading services.');
    }
    setLoading(false);
  }, [token, type]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const toggleService = (serviceId) => {
    if (readOnly) return;
    const updated = value.includes(serviceId)
      ? value.filter(id => id !== serviceId)
      : [...value, serviceId];
    onChange?.(updated);
  };

  return (
    <div className="competency-wrapper" style={{ marginTop: 20 }}>
      <div
        className="competency-header"
        onClick={() => setCollapsed(!collapsed)}
        style={{ cursor: 'pointer' , border : 'none'}}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiFileText size={18} />
          <h2 style={{ margin: 0, fontSize: 16 }}>Services</h2>
          {value.length > 0 && (
            <span style={{
              background: '#1a2a4d',
              color: '#fff',
              borderRadius: 10,
              padding: '1px 8px',
              fontSize: 11,
              fontWeight: 600,
            }}>
              {value.length} selected
            </span>
          )}
        </div>
        <span>{collapsed ? <FiChevronDown /> : <FiChevronUp />}</span>
      </div>

      {!collapsed && (
        <div className="sub-card-content" style={{ padding: '15px 20px' , marginBottom:0 , border: 'none' }}>
          {loading && <div style={{ color: '#888', fontSize: 13 }}>Loading services…</div>}
          {error && <div style={{ color: '#c0392b', fontSize: 13 }}>⚠ {error}</div>}
          
          {!loading && !error && services.length === 0 && (
            <div style={{ color: '#888', fontSize: 13 }}>No services found.</div>
          )}

          {!loading && !error && services.length > 0 && (
            <div className="grid-table" style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
              gap: '10px',
              padding: '10px 0'
            }}>
              {services.map(s => (
                <label key={s.value} className="checkbox-item" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 8, 
                  cursor: readOnly ? 'default' : 'pointer',
                  fontSize: 13,
                  color: '#333'
                }}>
                  <input
                    type="checkbox"
                    checked={value.includes(s.value)}
                    onChange={() => toggleService(s.value)}
                    disabled={readOnly}
                    style={{ cursor: readOnly ? 'default' : 'pointer' }}
                  />
                  {s.label}
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ServiceSelection;
